import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Space, Tooltip } from 'antd';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import useBreadcrumb from 'hooks/useBreadcrumb';
import NewTeamMemberFormDrawer from './components/NewTeamMemberFormDrawer';
import { TeamPageTabKey, TeamPageTabKeys } from '../types';
import { getActiveTeamPageTabKey } from '../utils';
import NewTeamActionsPopover from './components/NewTeamActionsPopover';
import useTeams from 'hooks/useTeams';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import { StyledBadge } from 'components/new-ui/base/status-indicator/styles';
import { TeamStatuses } from 'store/teams/types';

interface Props {
    children: ReactNode;
}

const TeamPage = ({ children }: Props) => {
    const { userInfo } = useSession();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const location = useLocation();
    const navigate = useNavigate();
    const { teamId } = useParams<{ teamId: string }>();
    const {
        team,
        getTeamById,
        archivationStatus,
        setArchivationStatus,
        invoicesCount,
        manifestsCount,
        jobsCount,
        getTeamInvoicesCount,
        getTeamManifestsCount,
        getTeamJobsCount,
    } = useTeams(teamId);

    const [activeTabKey, setActiveTabKey] = useState<TeamPageTabKey>(getActiveTeamPageTabKey(location));
    const [openPopoverButtonsActions, setOpenPopoverButtonsActions] = useState(false);
    const [showNewTeamMemberDrawer, setShowTeamMemberDrawer] = useState(false);

    const canCreateManifests = hasPermissions(userInfo, 'manifests', 'CREATE');
    const canCreateJobs = hasPermissions(userInfo, 'jobs', 'CREATE');
    const canCreateCustomers = hasPermissions(userInfo, 'customers', 'CREATE');
    const canCreateTeamMembers = hasPermissions(userInfo, 'teams', 'CREATE');

    const teamName = team?.name ?? '-';
    const isNotActiveTeam = team?.status !== TeamStatuses.ACTIVE || false;

    useEffect(() => {
        if (!team) {
            return;
        }

        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Teams', path: '../teams' },
            { breadcrumbName: teamName, path: '' },
        ]);
    }, [teamId, setBreadcrumbRoutes, teamName, team]);

    const tabsItems = useMemo(
        () => [
            {
                key: TeamPageTabKeys.MANIFESTS,
                label: (
                    <>Manifests {manifestsCount > 0 && <StyledBadge color="var(--gray-6)" count={manifestsCount} />}</>
                ),
            },
            {
                key: TeamPageTabKeys.JOBS,
                label: <>Jobs {jobsCount > 0 && <StyledBadge color="var(--gray-6)" count={jobsCount} />}</>,
            },
            { key: TeamPageTabKeys.PARTIES, label: 'Parties' },
            {
                key: TeamPageTabKeys.INVOICES,
                label: <>Invoices {invoicesCount > 0 && <StyledBadge color="var(--gray-6)" count={invoicesCount} />}</>,
            },
            { key: TeamPageTabKeys.DETAILS, label: 'Details' },
            { key: TeamPageTabKeys.MEMBERS, label: 'Members' },
        ],
        [invoicesCount, manifestsCount, jobsCount]
    );

    useEffect(() => {
        if (!team && teamId) {
            getTeamById(teamId);
        }
    }, [getTeamById, team, teamId]);

    // Get number of invoices, manifests and jobs for the team
    useEffect(() => {
        if (teamId) {
            getTeamInvoicesCount(teamId);
            getTeamManifestsCount(teamId);
            getTeamJobsCount(teamId);
        }
    }, [teamId, getTeamInvoicesCount, getTeamManifestsCount, getTeamJobsCount]);

    // Clear the archivation status on component unmount
    useEffect(() => {
        return () => {
            setArchivationStatus('active');
        };
    }, [setArchivationStatus]);

    if (!team) {
        return null;
    }

    return (
        <StyledPageWrapper>
            <PageHeader
                title={teamName}
                icon={null}
                showBackButton
                onBackButtonClick={() => navigate(-1)}
                sidebar={
                    <Space size={16}>
                        <ArchiveFilter value={archivationStatus} onChange={setArchivationStatus} />
                        {(canCreateManifests || canCreateJobs || canCreateCustomers || canCreateTeamMembers) && (
                            <Tooltip title={isNotActiveTeam ? 'The team must be activated first' : ''}>
                                <NewTeamActionsPopover
                                    disabled={isNotActiveTeam}
                                    open={openPopoverButtonsActions}
                                    setOpen={setOpenPopoverButtonsActions}
                                    onNewManifestClick={
                                        canCreateManifests
                                            ? () => navigate(`/manifests/new?teamId=${teamId}`)
                                            : undefined
                                    }
                                    onNewJobClick={
                                        canCreateJobs ? () => navigate(`/jobs/new?teamId=${teamId}`) : undefined
                                    }
                                    onNewTeamMemberClick={
                                        canCreateTeamMembers ? () => setShowTeamMemberDrawer(true) : undefined
                                    }
                                />
                                <span></span>
                            </Tooltip>
                        )}
                    </Space>
                }
            />

            <StyledTabs
                style={{ marginTop: 24 }}
                activeKey={activeTabKey}
                onChange={(key) => {
                    const baseUrl = `/teams/${teamId}`;
                    const to = key === TeamPageTabKeys.MANIFESTS ? baseUrl : `${baseUrl}/${key}`;
                    navigate(to, { replace: true });
                    setActiveTabKey(key as TeamPageTabKey);
                    // Set archivation status to active whenever we switch tabs
                    setArchivationStatus('active');
                }}
                items={tabsItems}
            />

            {children}

            {canCreateTeamMembers && (
                <NewTeamMemberFormDrawer
                    customerId={userInfo?.customerId}
                    open={showNewTeamMemberDrawer}
                    onClose={() => setShowTeamMemberDrawer(false)}
                    onSuccess={async () => {
                        await getTeamById(team.id);
                        setShowTeamMemberDrawer(false);
                    }}
                />
            )}
        </StyledPageWrapper>
    );
};

export default TeamPage;
